import React, { HTMLAttributes, useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Context } from "@xt/xtbase";
const { XtContext } = Context;
import store from "store";
import Storage from "utils/storage";
import Socket from "utils/socket/public";
import { get_kline } from "api/v4/market";
import { widget, ResolutionString } from "components/tradingview/v22/charting_library";

import tvConfig, { getTimezone } from "components/tradingview/v22/config";
import styles from "./index.module.scss";

/** K 线休市不产生空白：见 cfd/_cmpt/kline/tradingview/content 顶部注释。Mock 调试代码已注释，当前使用无休市 24x7。 */
// const KLINE_SESSION_STOCK = "0930-1600";
// const KLINE_TIMEZONE_STOCK = "America/New_York";
// const KLINE_SESSION_MOCK_KEY = "kline_session_stock_mock";
// function getKlineSessionStockMock(): boolean { ... }
// function setKlineSessionStockMock(useStock: boolean) { ... }

const TvIntervalToXtApi = {
  "1": "1m",
  "3": "3m",
  "5": "5m",
  "15": "15m",
  "30": "30m",
  "60": "1h",
  "120": "2h",
  "240": "4h",
  "360": "6h",
  "480": "8h",
  "720": "12h",
  "1D": "1d",
  "3D": "3d",
  "1W": "1w",
  "1M": "1M",
}; //tradingView内部周期对应xt接口请求参数，key: tv内部周期, value: 请求参数

import XtLoading from "components/xt/loading";

// interface TvWidgetProps extends IChartingLibraryWidget {
//   xtSymbol: string;
// }

interface KlineProps {
  s?: string; //symbol 交易对
  i?: string; //interval 周期间隔
  t: number; //开盘时间(time)，毫秒
  o: string; //开盘价(open)
  c: string; //收盘价(close)
  h: string; //最高价(high)
  l: string; //最低价(low)
  q: string; //成交量(quantity)
  v: string; //成交额(volume)
}

import { OptionTradingViewProps, getOptionTradingView, ActionTradingViewProps } from "../../index";
interface Props extends HTMLAttributes<HTMLDivElement> {
  option: WithUndefined<OptionTradingViewProps>;
  action: WithUndefined<ActionTradingViewProps>;
}

const Main: React.FC<Props> = ({ option, action }) => {
  const [appState] = React.useContext(XtContext);
  const { isH5 } = store.app;
  const { name, formatName } = store.market;

  const [loading, setLoading] = useState<boolean>(true);
  const [tvWidget, setTvWidget] = useState<any>();

  const themeChange = useCallback(
    (theme) => {
      if (!tvWidget) return;
      tvWidget.changeTheme(theme === "dark" ? "Dark" : "Light");
      setTimeout(() => {
        tvWidget.applyOverrides(tvConfig({ theme }).overrides);
      }, 0);
    },
    [tvWidget]
  );
  const onChartDataLoaded = useCallback(() => {
    console.log("【tradingView】new symbol's data has been received.");
  }, []);
  const tvDataFormat = useCallback((obj: KlineProps) => {
    return {
      time: obj.t,
      open: +obj.o,
      high: +obj.h,
      low: +obj.l,
      close: +obj.c,
      volume: +obj.q,
      volume2: +obj.v,
    };
  }, []);

  const getBars = useCallback((symbolInfo, resolution, periodParams, onHistoryCallback) => {
    // 股票 session 模式下 bar.time 需为 UTC 毫秒且对齐交易时段，库会折叠休市时段使 K 线连续无空白。
    const { to, countBack, firstDataRequest } = periodParams;
    const symbol = symbolInfo.ticker;
    const interval = TvIntervalToXtApi[resolution];
    get_kline({
      params: {
        symbol,
        interval,
        endTime: firstDataRequest ? undefined : to * 1000,
        limit: Math.min(countBack, 1000),
      },
    }).then((data: KlineProps[]) => {
      const ary: any = [];

      if (data && data.length) {
        data.map((obj) => {
          ary.unshift(tvDataFormat(obj));
        });
      }
      // console.log("ary=", ary);
      onHistoryCallback(ary, { noData: !ary.length });
    });
  }, []);
  const subscribeBars = useCallback((symbolInfo, resolution, onRealtimeCallback) => {
    // console.log("【TradingView】------->[subscribeBars]", { symbolInfo, resolution, onRealtimeCallback, subscriberUID, onResetCacheNeededCallback });
    const symbol = symbolInfo.ticker;
    const interval = TvIntervalToXtApi[resolution];
    const msg = {
      event: `kline@${symbol},${interval}`,
    };

    Socket.addChannel(msg, (data: KlineProps) => {
      onRealtimeCallback(tvDataFormat(data));
    });
  }, []);
  const unsubscribeBars = useCallback((subscriberUID) => {
    // console.log("【TradingView】------->[unsubscribeBars]", subscriberUID);
    const [symbol, interval] = subscriberUID.split("_#_");
    const msg = {
      event: `kline@${symbol},${TvIntervalToXtApi[interval]}`,
    };

    Socket.removeChannel(msg);
  }, []);

  //TradingView init
  useEffect(() => {
    const chartLocalConfig = Storage.get("tvChart");
    const tvCfg = tvConfig({ theme: appState.theme });
    const { interval } = getOptionTradingView(option);
    const tvConstructor = new widget({
      ...tvCfg,
      disabled_features: tvCfg.disabled_features.concat(isH5 ? ["widget_logo"] : []),
      container: "TradingView",
      symbol: formatName(name),
      locale: (() => {
        const obj = {
          "zh-CN": "zh",
          "zh-HK": "zh_TW",
          en: "en",
          ja: "ja",
          ko: "ko",
          ru: "ru",
          es: "es",
          tr: "tr",
        };
        return obj[appState.lang] || "en";
      })(),
      interval,
      saved_data: chartLocalConfig,
      datafeed: {
        onReady(callback) {
          // console.log("【TradingView】------->[onReady]");
          return new Promise((resolve) => {
            resolve({
              supported_resolutions: Object.keys(TvIntervalToXtApi),
            });
          }).then((data) => callback(data));
        },
        //通过商品名称解析商品信息
        resolveSymbol(symbolName, onSymbolResolvedCallback, onResolveErrorCallback) {
          return new Promise((resolve) => {
            const { name, currentConfig, formatName } = store.market;
            const symbolName = formatName(name);
            resolve({
              name: symbolName,
              full_name: symbolName,
              ticker: name,
              minmov: 1,
              pricescale: Math.pow(10, currentConfig.pricePrecision || 2),
              timezone: getTimezone(),
              has_intraday: true,
              has_daily: true,
              has_weekly_and_monthly: true,
              session: "24x7",
              volume_precision: 2,
              exchange: "Spikex",
            });
          })
            .then((data) => onSymbolResolvedCallback(data))
            .catch((err) => onResolveErrorCallback(err));
        },
        //当图表库需要由日期范围定义的历史K线片段时，将调用此函数。
        getBars,
        //订阅K线数据。图表库将调用onRealtimeCallback方法以更新实时数据。
        subscribeBars,
        //取消订阅K线数据
        unsubscribeBars,
      },
    } as any);

    tvConstructor.onChartReady(() => {
      setTvWidget(tvConstructor);
    });
  }, []);
  //TradingView ready
  useEffect(() => {
    if (!tvWidget) return;

    const { theme } = appState;
    if (tvWidget.getTheme().toLowerCase() !== theme) {
      themeChange(theme);
    }

    const chartLocalConfig = Storage.get("tvChart");
    const tvCfg = tvConfig({ theme });

    tvWidget.subscribe("undo_redo_state_changed", () => {
      // console.log("undo_redo_state_changed-=====");
      tvWidget.save((state) => {
        Storage.set("tvChart", state);
      });
    });

    if (chartLocalConfig) {
      tvWidget.applyOverrides(tvCfg.overrides);
    } else {
      (() => {
        tvWidget.chart().createStudy(
          "Moving Average",
          false,
          false,
          { length: 5 },
          {
            "plot.color": "#0075FF",
            "plot.linewidth": 1,
          }
        );
        tvWidget.chart().createStudy(
          "Moving Average",
          false,
          false,
          { length: 15 },
          {
            "plot.color": "#5C00F3",
            "plot.linewidth": 1,
          }
        );
        tvWidget.chart().createStudy(
          "Moving Average",
          false,
          false,
          { length: 30 },
          {
            "plot.color": "#FFC453",
            "plot.linewidth": 1,
          }
        );

        setTimeout(() => {
          console.log("tvWidget.save");
          tvWidget.save((state) => {
            Storage.set("tvChart", state);
          });
        }, 0);
      })();
    }

    const { interval, chartType } = getOptionTradingView(option);
    tvWidget.chart().setChartType(chartType);

    tvWidget.activeChart().executeActionById("chartReset");

    const symbolInterval = tvWidget.symbolInterval();
    if (!(formatName(name) === symbolInterval.symbol && interval === symbolInterval.interval)) {
      tvWidget.setSymbol(name, interval as ResolutionString, onChartDataLoaded);
    }

    setLoading(false);
  }, [tvWidget]);

  useEffect(() => {
    // console.log("【tradingView】useEffect====", name, option, tvWidget);
    if (!tvWidget) return;
    // console.log("【tradingView】setSymbol====", name, option, tvWidget);

    const { interval, chartType } = getOptionTradingView(option);
    // 币种切换时先重置图表数据，避免上一币种价格残留在图表中
    try {
      if (tvWidget.activeChart && typeof tvWidget.activeChart === "function") {
        const activeChart = tvWidget.activeChart();
        if (activeChart && typeof activeChart.resetData === "function") {
          activeChart.resetData();
        }
        activeChart.executeActionById("chartReset");
      }
    } catch (e) {
      console.warn("【tradingView】resetData/chartReset on symbol change", e);
    }
    tvWidget.setSymbol(name, interval as ResolutionString, onChartDataLoaded);
    tvWidget.chart().setChartType(chartType);
  }, [name, option]);
  useEffect(() => {
    // console.log("appState.theme=",appState.theme);
    if (!tvWidget) return;
    const { theme } = appState;
    themeChange(theme);
  }, [appState.theme]);
  useEffect(() => {
    if (!tvWidget || !action) return;
    const { actionId } = action;
    actionId && tvWidget.chart().executeActionById(actionId);
  }, [action]);

  return (
    <div className={styles.main}>
      {/* 股票 session Mock UI 已注释，当前使用 24x7 */}
      <div id="TradingView" className={styles.tv}></div>
      {loading && <XtLoading className={styles.loading} />}
    </div>
  );
};

export default observer(Main);
